import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faRotateRight, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@/components/ui/button';
import { SearchBar } from '@/components/shared/SearchBar';
import { useDebounce } from '@/hooks/use-debounce';
import {
  bulkImportService,
  type BulkImportSession,
  type BulkImportRowClassification,
  type BulkImportRowListItem,
} from '@/services/bulk-import.service';
import { CLASSIFICATION_LABELS } from './bulk-import-ui.constants';
import { BulkImportRowTable } from './BulkImportRowTable';
import { BulkImportRowDetailModal } from './BulkImportRowDetailModal';
import { BulkImportFinalImportSection } from './BulkImportFinalImportSection';
import { toast } from '@/lib/swal';
import { getApiErrorMessage } from '@/lib/api-error';

const PAGE_SIZE = 20;

const SUMMARY_CARDS: {
  key: 'total' | BulkImportRowClassification;
  label: string;
}[] = [
  { key: 'total', label: 'Total Rows' },
  { key: 'PASS_EXISTING', label: CLASSIFICATION_LABELS.PASS_EXISTING },
  { key: 'PASS_NEW', label: CLASSIFICATION_LABELS.PASS_NEW },
  { key: 'WARNING', label: CLASSIFICATION_LABELS.WARNING },
  { key: 'FAIL', label: CLASSIFICATION_LABELS.FAIL },
  { key: 'CONFLICT', label: CLASSIFICATION_LABELS.CONFLICT },
];

function countFor(session: BulkImportSession, key: (typeof SUMMARY_CARDS)[number]['key']): number {
  switch (key) {
    case 'total':
      return session.totalRows;
    case 'PASS_EXISTING':
      return session.passExistingCount;
    case 'PASS_NEW':
      return session.passNewCount;
    case 'WARNING':
      return session.warningCount;
    case 'FAIL':
      return session.failCount;
    case 'CONFLICT':
      return session.conflictCount;
  }
}

interface BulkImportReviewStageProps {
  session: BulkImportSession;
  onSessionChanged: () => void;
}

export function BulkImportReviewStage({ session, onSessionChanged }: BulkImportReviewStageProps) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('rowPage') ?? '1');
  const classification = (searchParams.get('classification') ?? '') as
    BulkImportRowClassification | '';
  const [searchInput, setSearchInput] = useState(searchParams.get('rowSearch') ?? '');
  const debouncedSearch = useDebounce(searchInput, 350);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [openRow, setOpenRow] = useState<BulkImportRowListItem | null>(null);

  const updateParams = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setSearchParams(next, { replace: true });
  };

  const { data, isLoading } = useQuery({
    queryKey: ['bulk-import-rows', session.id, page, classification, debouncedSearch],
    queryFn: () =>
      bulkImportService.listRows(session.id, {
        page,
        pageSize: PAGE_SIZE,
        classification: classification || undefined,
        search: debouncedSearch || undefined,
      }),
  });

  const canAct = session.status === 'REVIEW_REQUIRED' || session.status === 'PARTIALLY_COMPLETED';

  const invalidateRows = () =>
    void queryClient.invalidateQueries({ queryKey: ['bulk-import-rows', session.id] });

  const revalidateSelectedMutation = useMutation({
    mutationFn: () => bulkImportService.revalidate(session.id, { rowIds: Array.from(selectedIds) }),
    onSuccess: (result) => {
      toast.success(`Revalidated ${result.revalidatedRowCount} row(s)`);
      setSelectedIds(new Set());
      invalidateRows();
      onSessionChanged();
    },
    onError: (e: Error) => toast.error(getApiErrorMessage(e, 'Failed to revalidate selected rows')),
  });

  const revalidateAllMutation = useMutation({
    mutationFn: () => bulkImportService.revalidate(session.id, {}),
    onSuccess: (result) => {
      toast.success(`Revalidated ${result.revalidatedRowCount} row(s)`);
      setSelectedIds(new Set());
      invalidateRows();
      onSessionChanged();
    },
    onError: (e: Error) => toast.error(getApiErrorMessage(e, 'Failed to revalidate session')),
  });

  const rows = useMemo(() => data?.data ?? [], [data]);

  const toggleRow = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAllOnPage = (checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const r of rows) {
        if (checked) next.add(r.id);
        else next.delete(r.id);
      }
      return next;
    });

  const anyRevalidating = revalidateSelectedMutation.isPending || revalidateAllMutation.isPending;

  return (
    <div className="space-y-5">
      {session.status === 'PARTIALLY_COMPLETED' && (
        <div className="flex items-start gap-3 rounded-lg border border-orange-500/30 bg-orange-500/10 px-4 py-3">
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            className="mt-0.5 h-4 w-4 text-orange-700"
          />
          <div className="text-sm text-orange-800">
            <p className="font-medium">This session is partially completed.</p>
            <p className="mt-0.5 text-xs">
              Some rows were already committed in an earlier import attempt. Remaining rows can
              still be corrected and revalidated below.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {SUMMARY_CARDS.map((card) => {
          const isActive = card.key !== 'total' && classification === card.key;
          return (
            <button
              key={card.key}
              type="button"
              onClick={() =>
                updateParams({
                  classification: card.key === 'total' ? '' : isActive ? '' : card.key,
                  rowPage: '1',
                })
              }
              className={`rounded-lg border px-3 py-3 text-left transition-colors ${
                isActive ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-muted/30'
              }`}
              aria-pressed={isActive}
            >
              <p className="text-xs text-muted-foreground">{card.label}</p>
              <p className="mt-1 text-xl font-semibold text-foreground">
                {countFor(session, card.key)}
              </p>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SearchBar
          value={searchInput}
          onChange={(v) => {
            setSearchInput(v);
            updateParams({ rowSearch: v, rowPage: '1' });
          }}
          placeholder="Search by serial #, asset #, or equipment name…"
        />
        <div className="flex items-center gap-2">
          {selectedIds.size > 0 && (
            <Button
              variant="secondary"
              size="sm"
              disabled={!canAct || anyRevalidating}
              onClick={() => revalidateSelectedMutation.mutate()}
            >
              {revalidateSelectedMutation.isPending ? (
                <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3 w-3 animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faRotateRight} className="mr-2 h-3 w-3" />
              )}
              Revalidate Selected ({selectedIds.size})
            </Button>
          )}
          <Button
            size="sm"
            disabled={!canAct || anyRevalidating}
            onClick={() => revalidateAllMutation.mutate()}
          >
            {revalidateAllMutation.isPending ? (
              <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3 w-3 animate-spin" />
            ) : (
              <FontAwesomeIcon icon={faRotateRight} className="mr-2 h-3 w-3" />
            )}
            Revalidate Entire Session
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <FontAwesomeIcon
            icon={faSpinner}
            className="h-6 w-6 animate-spin text-muted-foreground"
          />
        </div>
      ) : (
        <BulkImportRowTable
          rows={rows}
          total={data?.total ?? 0}
          page={data?.page ?? page}
          pageSize={data?.pageSize ?? PAGE_SIZE}
          onPageChange={(p) => updateParams({ rowPage: String(p) })}
          selectedIds={selectedIds}
          onToggleRow={toggleRow}
          onToggleAllOnPage={toggleAllOnPage}
          onOpenRow={setOpenRow}
        />
      )}

      <BulkImportRowDetailModal
        sessionId={session.id}
        sessionStatus={session.status}
        rowId={openRow?.id ?? null}
        rowNumber={openRow?.rowNumber}
        onClose={() => setOpenRow(null)}
        onChanged={() => {
          invalidateRows();
          onSessionChanged();
        }}
      />

      <BulkImportFinalImportSection session={session} onSessionChanged={onSessionChanged} />
    </div>
  );
}
